import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Plus, X, MapPin } from "lucide-react";

const EMPTY_LOC = { label: "", address: "", city: "", state: "", zip_code: "", phone: "" };

/**
 * Reusable multi-location editor.
 * Props:
 *   value: { has_multiple_locations, additional_locations }
 *   onChange: (patch) => void   // patch is a partial of {has_multiple_locations, additional_locations}
 */
export default function LocationsSection({ value, onChange, label = "This client has multiple locations" }) {
  const enabled = !!value.has_multiple_locations;
  const locs = value.additional_locations || [];

  const setEnabled = (v) => {
    // when enabling for the first time, seed with one blank location so the form is immediately fillable
    if (v && locs.length === 0) {
      onChange({ has_multiple_locations: true, additional_locations: [{ ...EMPTY_LOC }] });
    } else {
      onChange({ has_multiple_locations: !!v });
    }
  };

  const updateLoc = (i, patch) => {
    const next = locs.map((l, idx) => idx === i ? { ...l, ...patch } : l);
    onChange({ additional_locations: next });
  };
  const addLoc = () => onChange({ additional_locations: [...locs, { ...EMPTY_LOC }] });
  const removeLoc = (i) => onChange({ additional_locations: locs.filter((_, idx) => idx !== i) });

  return (
    <div className="col-span-2 border border-[var(--border)] rounded-lg p-3 bg-[var(--bg-soft)]" data-testid="locations-section">
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox checked={enabled} onCheckedChange={setEnabled} data-testid="multi-locations-checkbox" />
        <span className="text-sm font-semibold">{label}</span>
      </label>
      {enabled && (
        <div className="mt-3 space-y-3" data-testid="locations-editor">
          <p className="text-xs text-gray-500">Address #1 is the primary address entered above. Add each additional site below (#2, #3…).</p>
          {locs.map((loc, i) => (
            <div key={i} className="border border-[var(--border)] rounded p-3 bg-white relative" data-testid={`location-card-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="label-eyebrow flex items-center gap-1"><MapPin size={11} /> Address #{i + 2}</span>
                <button type="button" onClick={() => removeLoc(i)}
                        className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                        data-testid={`remove-location-${i}`}>
                  <X size={12} /> Remove
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Location label</Label>
                  <Input value={loc.label} onChange={(e) => updateLoc(i, { label: e.target.value })}
                         placeholder={`e.g. Address #${i + 2} — Uptown Shop`} data-testid={`loc-${i}-label`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Street address</Label>
                  <Input value={loc.address} onChange={(e) => updateLoc(i, { address: e.target.value })}
                         data-testid={`loc-${i}-address`} />
                </div>
                <div>
                  <Label className="text-xs">City</Label>
                  <Input value={loc.city} onChange={(e) => updateLoc(i, { city: e.target.value })}
                         data-testid={`loc-${i}-city`} />
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Input value={loc.state} onChange={(e) => updateLoc(i, { state: e.target.value })}
                         data-testid={`loc-${i}-state`} />
                </div>
                <div>
                  <Label className="text-xs">Zip</Label>
                  <Input value={loc.zip_code} onChange={(e) => updateLoc(i, { zip_code: e.target.value })}
                         data-testid={`loc-${i}-zip`} />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={loc.phone} onChange={(e) => updateLoc(i, { phone: e.target.value })}
                         data-testid={`loc-${i}-phone`} />
                </div>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addLoc} data-testid="add-location-btn">
            <Plus size={12} className="mr-1" /> Add another location
          </Button>
        </div>
      )}
    </div>
  );
}
